import { LanguageNameSpaces, TradeData } from "@shared/types";
import MobileTradeCard from "features/dashboard/MobileDashboard/MobileTradeCard";
import React from "react";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Tag from "common/Tags/Tag";
import styled, { useTheme } from "styled-components";
import { Flame } from "lucide-react";
import { Pill } from "common/NewLook";
import { NewLookMobileTradeCard } from "features/dashboard/OpenTrades/NewLookTradeCard.tsx";
import { getMobileSectionTitleProps } from "features/dashboard/common/styles";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { HOT_TRADES_HELPER_TOOLTIP } from "@shared/common/Tooltips/constants";
import { FireFilled } from "features/dashboard/common/styles";
import FlatList from "common/Lists/FlatList";
import { BOTTOM_NAVIGATOR_TRADE_CARD_HEIGHT } from "features/dashboard/common/constants";
import { useDashboardTradeCards } from "@shared/features/dashboard/hooks";
import TradesEmpty from "features/trades/TradesEmpty";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

const MobileTradesCards = () => {
  const theme = useTheme();
  const sectionTitleProps = getMobileSectionTitleProps(theme.isNewLook);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);

  const { sortedData, numOfHotTrades, getOpenTradesStatus } =
    useDashboardTradeCards({});

  const title = (
    <Flex $justifyContent="flex-start" style={{ padding: "0 16px 16px" }}>
      <Title {...sectionTitleProps}>
        {t("open_trades.mobile_title_with_count", {
          count: sortedData?.length || 0,
        })}
      </Title>
      {theme.isNewLook ? (
        <NewLookHotPill tone="blue" icon={Flame}>
          {t("hot_trades.tag_simple", { count: numOfHotTrades })}
          <HelperTooltip
            {...HOT_TRADES_HELPER_TOOLTIP}
            description={tCommon(HOT_TRADES_HELPER_TOOLTIP.description)}
            title={tCommon(HOT_TRADES_HELPER_TOOLTIP.title)}
            containerStyle={{ marginLeft: 2 }}
            iconStyle={{ color: "inherit" }}
          />
        </NewLookHotPill>
      ) : (
        <Tag
          color={theme.colors.secondaryGrey}
          style={{
            whiteSpace: "nowrap",
            marginLeft: 8,
            padding: "2px 4px",
          }}
        >
          <FireFilled style={{ marginLeft: 0 }} />
          {t("hot_trades.tag_simple", { count: numOfHotTrades })}
          <HelperTooltip
            {...HOT_TRADES_HELPER_TOOLTIP}
            description={tCommon(HOT_TRADES_HELPER_TOOLTIP.description)}
            title={tCommon(HOT_TRADES_HELPER_TOOLTIP.title)}
            containerStyle={{ marginLeft: 4 }}
            iconStyle={{
              color: theme.colors.primaryGrey,
              top: 1,
              position: "relative",
            }}
          />
        </Tag>
      )}
    </Flex>
  );

  const renderTradeCard = ({ item }: { item: TradeData }) => {
    if (theme.isNewLook)
      return <NewLookMobileTradeCard key={item.id} tradeData={item} />;
    return (
      <MobileTradeCard
        key={item.id}
        tradeData={item}
        isLoading={getOpenTradesStatus.isLoading}
      />
    );
  };

  return (
    <>
      {title}
      {sortedData.length ? (
        <FlatList<TradeData>
          data={sortedData || []}
          horizontal={true}
          renderContent={renderTradeCard}
          itemHeight={
            theme.isNewLook
              ? NEW_LOOK_MOBILE_TRADE_CARD_HEIGHT
              : BOTTOM_NAVIGATOR_TRADE_CARD_HEIGHT
          }
        />
      ) : (
        <TradesEmpty
          isOpenTrades={true}
          iconWidth={60}
          style={{
            padding: "28px 0",
            maxHeight: BOTTOM_NAVIGATOR_TRADE_CARD_HEIGHT,
          }}
          titleStyle={{
            fontSize: theme.typography.fontSizes.S,
            marginTop: 4,
          }}
          buttonStyle={{
            fontSize: theme.typography.fontSizes.S,
            padding: "4px 20px",
            marginTop: 8,
            height: "auto",
          }}
        />
      )}
    </>
  );
};

export default React.memo(MobileTradesCards);

const NEW_LOOK_MOBILE_TRADE_CARD_HEIGHT = 216;
const NewLookHotPill = styled(Pill)`
  margin-left: 8px;
`;
